// Central "tap a notification -> land on the right tab/section" logic.
//
// Every entry point (native push tap, native local notification tap, web push
// click via the service worker, ?notification= deep link) calls
// handleNotificationTap(data). The resolved target is kept in sessionStorage
// (so it survives a cold start while the app is still loading) and announced
// with a window event (so an already-open app reacts immediately). CoachView /
// PlayerView / App consume it with takeNavTarget().

const STORAGE_KEY = 'notificationNavTarget';
const EVENT_NAME = 'notificationNavTarget';

const COACH_ROLES = ['coach', 'headcoach'];

// ── Coach / headcoach ─────────────────────────────────────────────
const COACH_BY_TYPE = {
  // Player updates (questions, shared matches, thoughts)
  player_update_question: { tab: 'updates' },
  player_update_match: { tab: 'updates' },
  player_update_thought: { tab: 'updates' },
  player_update: { tab: 'updates' },

  // Q&A
  question: { tab: 'questions' },
  question_coach_answered: { tab: 'questions' },
  question_coach_resolved: { tab: 'questions' },

  // Bookings: incoming requests / cancellations / waitlist activity
  booking_request: { tab: 'bookings', section: 'requests' },
  booking_player_canceled: { tab: 'bookings', section: 'requests' },
  cancellation_request: { tab: 'bookings', section: 'requests' },
  guest_booking: { tab: 'bookings', section: 'requests' },
  booking_rescheduled_coach: { tab: 'bookings', section: 'requests' },
  reschedule_declined_coach: { tab: 'bookings', section: 'requests' },
  waitlist_joined_coach: { tab: 'bookings', section: 'slots' },
  waitlist_accepted_coach: { tab: 'bookings', section: 'requests' },
  waitlist_declined_coach: { tab: 'bookings', section: 'slots' },
  waitlist_left_coach: { tab: 'bookings', section: 'slots' },
  coaching_plan_booked: { tab: 'bookings', section: 'plans' },
  plan_session_scheduled_coach: { tab: 'bookings', section: 'plans' },
  recurring_slots_generated: { tab: 'bookings', section: 'recurring' },
  lesson_reminder: { tab: 'bookings', section: 'requests' },

  // Lesson-type waitlists
  lesson_type_waitlist_joined: { tab: 'type-waitlist' },
  type_waitlist_joined_coach: { tab: 'type-waitlist' },
  type_waitlist_left_coach: { tab: 'type-waitlist' },
  type_waitlist_accepted_coach: { tab: 'type-waitlist' },
  type_waitlist_declined_coach: { tab: 'type-waitlist' },

  // Coach partnerships / student sharing
  coach_share_invitation: { tab: 'partnerships' },
  coach_share_accepted: { tab: 'partnerships' },
  share_accepted_by_coach: { tab: 'partnerships' },
  share_accepted: { tab: 'partnerships' },
  student_shared: { tab: 'partnerships' },
  share_revoked: { tab: 'partnerships' },

  // Students
  headcoach_new_user: { tab: 'students' },
  priority_skill_unlocked: { tab: 'students', studentTab: 'skills' },
  priority_skill_deadline_due: { tab: 'students', studentTab: 'skills' },
  personal_note_access_granted: { tab: 'students', studentTab: 'personal' },
  personal_note_access_revoked: { tab: 'students', studentTab: 'personal' },
  lesson_briefing: { tab: 'students' }, // refined below when a note/student is known
  spanish_review: { tab: 'students', studentTab: 'spanish' },

  // Headcoach task lists: the coach answers in the Tasks tab, the headcoach sees the answers in the Manager tab
  task_list: { appTab: 'messages' }, // the Tasks tab (tab id kept from the old Messages tab)
  chat_message: { tab: 'questions' }, // the chat took over the old Questions tab
  task_list_reply: { appTab: 'manager' },

  // Head coach morning / night brief lives on top of the Manager tab; players finishing homework show in the Homework tab
  headcoach_brief: { appTab: 'manager' },
  homework_done: { appTab: 'homework' },

  // Account
  coach_assigned_headcoach: { appTab: 'settings' }
};

const COACH_BY_SCREEN = {
  updates: { tab: 'updates' },
  bookings: { tab: 'bookings', section: 'requests' },
  partnerships: { tab: 'partnerships' },
  students: { tab: 'students' },
  'priority-progress': { tab: 'students', studentTab: 'skills' },
  skills: { tab: 'students', studentTab: 'skills' },
  'personal-notes': { tab: 'students', studentTab: 'personal' },
  settings: { appTab: 'settings' }
};

// ── Player / manager ──────────────────────────────────────────────
// Bookings live under the player's "Browse Coaching" tab; `sub` says which
// collapsible section to open there.
const BOOKINGS = { tab: 'browse-coaching', sub: 'my-bookings' };

const PLAYER_BY_TYPE = {
  // Notes from the coach
  lesson_note_added: { tab: 'notes' },
  coach_note_added: { tab: 'notes' },
  new_note: { tab: 'notes' },
  photo_note_received: { tab: 'notes' },
  photo_added_to_note: { tab: 'notes' },
  personal_note_comment: { tab: 'notes', notesSource: 'personal' },

  // Chat lives in the Updates tab
  chat_message: { tab: 'updates' },

  // Homework from the coach
  homework_assigned: { tab: 'homework' },

  // Q&A / updates
  question_reply: { tab: 'updates' },
  question_resolved: { tab: 'updates' },
  player_update_answered: { tab: 'updates' },

  // Skills
  skill_update: { tab: 'skills' },
  priority_skills_assigned: { tab: 'skills' },
  priority_skill_unlocked: { tab: 'skills' },
  player_priority_unlocked: { tab: 'skills' },

  // Bookings
  booking_approved: BOOKINGS,
  booking_denied: BOOKINGS,
  booking_canceled: BOOKINGS,
  booking_player_removed: BOOKINGS,
  booking_rescheduled: BOOKINGS,
  reschedule_options: BOOKINGS,
  cancellation_approved: BOOKINGS,
  cancellation_denied: BOOKINGS,
  coaching_plan_confirmed: { tab: 'browse-coaching', sub: 'my-plans' },
  lesson_reminder: BOOKINGS,
  waitlist_joined: BOOKINGS,
  waitlist_offered: BOOKINGS,
  waitlist_expired: BOOKINGS,
  lesson_type_waitlist_offered: BOOKINGS,
  lesson_type_waitlist_joined: BOOKINGS,
  dormant_player_ping: { tab: 'browse-coaching', sub: 'browse' },
  slot_promotion: { tab: 'browse-coaching' },
  player_request_accepted: { tab: 'skills' },

  // Padel Espanol
  spanish_today: { tab: 'spanish' },
  spanish_homework: { tab: 'spanish' },
  spanish_progress: { tab: 'spanish' }
};

const PLAYER_BY_SCREEN = {
  bookings: BOOKINGS,
  updates: { tab: 'updates' },
  notes: { tab: 'notes' },
  lessons: { tab: 'notes' },
  'personal-notes': { tab: 'notes', notesSource: 'personal' },
  skills: { tab: 'skills' },
  spanish: { tab: 'spanish' },
  settings: { tab: 'settings' }
};

/**
 * Turn a notification's data payload into a navigation target for `role`.
 * Returns null when nothing sensible can be derived.
 */
export function resolveNavTarget(data = {}, role) {
  const isCoach = COACH_ROLES.includes(role);
  const byType = isCoach ? COACH_BY_TYPE : PLAYER_BY_TYPE;
  const byScreen = isCoach ? COACH_BY_SCREEN : PLAYER_BY_SCREEN;

  // Lesson briefing showing an unassigned Pocket summary -> the Pocket notes screen
  let base = (isCoach && data.screen === 'saved-notes')
    ? { appTab: 'saved-notes' }
    : (byType[data.type] || byScreen[data.screen] || null);
  if (!base) return null;

  const target = { ...base, role: isCoach ? 'coach' : 'player' };
  if (isCoach && data.pocketNoteId) target.pocketNoteId = data.pocketNoteId;
  if (isCoach && data.listId) target.listId = data.listId;

  if (isCoach) {
    // Lesson briefing pointing at a specific student / note
    if (data.type === 'lesson_briefing' || data.screen === 'student-note') {
      target.tab = 'students';
      if (data.playerId) target.playerId = data.playerId;
      if (data.noteId) target.noteId = data.noteId;
      target.studentTab = 'notes';
    }
    // Update / student notifications carry the student they are about
    if (data.playerId && (target.tab === 'updates' || target.tab === 'students')) {
      target.playerId = data.playerId;
    }
    if (data.type === 'spanish_review' && data.logId) target.logId = data.logId;
  } else if (data.noteId && target.tab === 'notes') {
    target.noteId = data.noteId;
  }

  return target;
}

export function currentRole() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.role || null;
  } catch (e) {
    return null;
  }
}

/** Entry point used by every notification tap source. */
export function handleNotificationTap(data, role = currentRole()) {
  if (!data) return;
  const target = resolveNavTarget(data, role);
  if (!target) return;

  // A coaching request: the Accept / Deny pop-up opens on top of whatever is showing
  if (data.type === 'booking_request' && data.bookingId && !data.headcoachCopy) {
    try { sessionStorage.setItem('focusBookingRequest', String(data.bookingId)); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event('focusBookingRequest'));
  }
  // The head coach brief: show the one (morning / night) the notification was about
  if (data.type === 'headcoach_brief' && data.brief) {
    try { sessionStorage.setItem('focusBrief', String(data.brief)); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event('focusBrief'));
  }

  // A chat message: remember whose conversation to open once the chat screen is showing
  if (data.type === 'chat_message' && data.fromId) {
    try { sessionStorage.setItem('focusChatWith', String(data.fromId)); } catch (e) { /* ignore */ }
    window.dispatchEvent(new Event('focusChatWith'));
  }

  target.nonce = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
  } catch (e) {
    // sessionStorage unavailable - the event below still reaches a live app
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: target }));
}

/** Read (and clear) the pending target if it is meant for `area`. */
export function takeNavTarget(area) {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const target = JSON.parse(raw);
    if (target.role !== area) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    return target;
  } catch (e) {
    return null;
  }
}

export function peekNavTarget() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

/** Subscribe to taps that arrive while the app is already open. Returns unsubscribe. */
export function onNavTarget(callback) {
  const listener = () => callback();
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}

/** Encode/decode data for the service-worker -> page hand-off via URL. */
export function readNotificationFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('notification');
    if (!raw) return null;
    params.delete('notification');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    return JSON.parse(decodeURIComponent(escape(atob(raw))));
  } catch (e) {
    return null;
  }
}
